import * as React from "react";
import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { getBrowserTimeZone, isStagingEnv, ORGANIZATION_STATUS_NAME, SiteKey, isOrganizationStatus } from "../../utils/constants";
import styles from "../../assets/styles/componentStyles/login.module.scss";
import { Divider, IconButton, InputLabel, Box, Grid, Button, Typography, Chip } from "@mui/material";
import GlobalButton from "../utility/globalButton/button";
import { UserContext, UserContextPayload } from "../../context/userContext";
import globalStyles from "../../assets/styles/global.module.scss";
import GlobalTextField from "../utility/globalTextfield";
import chronoxWhiteLogo from "../../assets/images/global/Chronox - White logo.svg";
import googleIcon from "../../assets/google.svg";
import edit from "../../assets/edit.png";
import VerificationComponent from "../utility/verificationComponent";
import back from "../../assets/loginBackArrow.svg";
import agent from "../../services/api";
import chronoxLogo from "../../assets/images/global/Chronox Logo.png";
import points from "../../assets/images/global/points.svg";
import meta from "../../assets/images/global/meta-verified.svg";
import rightImage from "../../assets/images/global/login-bg-right.svg";
import { getErrorMessage, isDev } from "../../utils/constants";
import { CommonResponseModel, CommonResponseSecondaryModel, RegisterDataResponseModel, SendOTPResponseDataModel } from "../../models/axiosModel";
import { getVendorField } from "../../utils/whiteLabelling";
import validator from "validator";
import { connectSocket } from "../../utils/socketIo";
import { useGenericStore } from "../../store/genericStore";
import { useShallow } from "zustand/react/shallow";

const LogoComponent = () => (
  <img src={edit} alt="Logo" width="17px" height="17px" />
);

declare global {
  interface Window {
    google: any;
  }
}


export default function Login() {
  // Inside your component

  let navigate = useNavigate();

  const { SendLoginOTP, Login, loginWithPassword, setFlagLoading, UserMe, OneTapLoginAuthCallback, userDetail, Register, flagLoading, handleError, vendorData, setFlagUpdateUserDetailPopup, setFlagViewUserDetailPopup, setShowOrganizationPausedAlert } = useContext(
    UserContext
  ) as UserContextPayload;

  const { showSuccess, removeSuccessSnackbar, showError, removeErrorSnackbar } = useGenericStore(useShallow((state) => ({
    showSuccess: state.showSuccess,
    removeSuccessSnackbar: state.removeSuccessSnackbar,
    showError: state.showError,
    removeErrorSnackbar: state.removeErrorSnackbar
  })))

  const [user, setUser] = useState({
    email: "",
    google_recaptcha_token: "",
  });

  const [otp, setOtp] = useState("");
  const [flagInvalidOtp, setInvalidOtp] = useState(false);
  const [flagEmailValid, setFlagEmailValid] = useState(true);
  const [flagShowVerificationBlock, setFlagShowVerificationBlock] =
    useState(false);
  const [flagShowPasswordBlock, setFlagShowPasswordBlock] = useState<boolean>(false);
  const [flagTimerActive, setFlagTimerActive] = useState(false);
  const [seconds, setSeconds] = useState(60);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const PASSWORD_LOGIN_CODE = 9447;

  useEffect(() => {
    let timerId: any;
    if (flagTimerActive && seconds > 0) {
      timerId = setInterval(() => {
        setSeconds(seconds - 1);
      }, 1000);
    }
    if (seconds === 0) {
      setFlagTimerActive(false);
    }
    return () => clearInterval(timerId);
  }, [flagTimerActive, seconds]);

  const handleAuth = async () => {
    const payload = {
      user_email: user.email,
      google_recaptcha_token: user.google_recaptcha_token,
      chatbot_flag: true,
      timezone: getBrowserTimeZone()
    }
    setFlagLoading({ ...flagLoading, flagRoundLoader: true });;
    const response: CommonResponseModel<RegisterDataResponseModel> | CommonResponseSecondaryModel<SendOTPResponseDataModel> = await Register(payload);
    setFlagLoading({ ...flagLoading, flagRoundLoader: false });;
    if (response.status) {
      if (response.data && 'message' in response.data) {
        let message = response.data.message
        const responseCode = Number((response.data as SendOTPResponseDataModel).code);

        if (responseCode === PASSWORD_LOGIN_CODE) {
          setFlagShowPasswordBlock(true);
          setFlagShowVerificationBlock(false);
          return;
        }

        showSuccess(message)
        setInvalidOtp(false);
        setOtp("");
        handleResetTimer();
        handleStartTimer();
        setFlagShowPasswordBlock(false);
        setFlagShowVerificationBlock(true);
      }
    } else {
      recaptchaRef.current?.reset();

      handleError(response);
    }
  }

  const handleReSendOtp = async () => {
    setFlagLoading({ ...flagLoading, flagRoundLoader: true });;
    const payload = {
      user_email: user.email,
      google_recaptcha_token: user.google_recaptcha_token,
      resend_flag: true
    };
    const response = await SendLoginOTP(payload);
    setFlagLoading({ ...flagLoading, flagRoundLoader: false });;
    if (response.status) {
      if (response.data) {
        showSuccess(getErrorMessage(response.data.code, isDev(userDetail), response))
        setInvalidOtp(false);
        setOtp("");
        handleResetTimer();
        handleStartTimer();
        if(flagShowPasswordBlock){
          setFlagShowPasswordBlock(false);
        }
        setFlagShowVerificationBlock(true);
      }
    } else {
      recaptchaRef.current?.reset();
      handleError(response);
      setUser({ ...user, google_recaptcha_token: "" })
    }
  };

  const hideGoogleOneTapLoginPopup = () => {
    const googleAccountId = window.google?.accounts?.id; 
    if(googleAccountId){
      googleAccountId.disableAutoSelect(); // Prevent Google One Tap Login Feature from running on an application after successfull login.
      googleAccountId.cancel(); // If One Tap is Already visible, this cancel function cancel(Hide) it.
    }
  }

  const handlePostLoginSuccess = async () => {
    removeSuccessSnackbar();

    hideGoogleOneTapLoginPopup();

    const res = await UserMe(); 
    if(res.status){ 
      const updatedUserDetail = res.data?.[0];
      if(updatedUserDetail  && !updatedUserDetail.flag_user_anonymous && res.status && (!updatedUserDetail?.user_first_name || updatedUserDetail?.user_first_name?.trim()?.length == 0)){
         setFlagUpdateUserDetailPopup(true); 
         setFlagViewUserDetailPopup(false);
      }
    } else {
      handleError(res);
    }
    
    if(res.status && res.data?.[0]?.access_token){
      connectSocket(res.data?.[0]?.access_token, vendorData);
    }

    if(res.status && res.data?.[0]){
      if(isOrganizationStatus(res.data?.[0], ORGANIZATION_STATUS_NAME.PAUSED)){
          setShowOrganizationPausedAlert(true);
      }
    }

    const searchParams = new URLSearchParams(window.location.search); let redirect = searchParams.get("redirect")
    if (redirect) {
      setTimeout(() => {
        navigate(redirect)
      }, 500)
    } else {
      navigate("/");
    }
  };

  const handleLogin = async (credential: any) => {
    setFlagLoading({ ...flagLoading, flagRoundLoader: true, flagContentGenerating: false });

    if (flagShowPasswordBlock) {
      const payload = {
        user_email: user.email,
        password: credential,
      };
      const response = await loginWithPassword(payload);
      setFlagLoading({ ...flagLoading, flagRoundLoader: false, flagContentGenerating: false });
      if (response.status) {
        if (response.data) {
          await handlePostLoginSuccess();
        }
      } else {
        handleError(response);
        recaptchaRef.current?.reset();
        setUser({ ...user, google_recaptcha_token: "" });
      }
      return;
    }

    setInvalidOtp(false)
    const payload = {
      user_email: user.email,
      authentication_code: credential,
    };
    const response = await Login(payload);
    setFlagLoading({ ...flagLoading, flagRoundLoader: false, flagContentGenerating: false });
    if (response.status) {
      if (response.data) {
        await handlePostLoginSuccess();
      }
    } else {
      handleError(response);
      setOtp("")
      recaptchaRef.current?.reset();
      setUser({ ...user, google_recaptcha_token: "" })
      setInvalidOtp(true);
    }
  };

  const handleBackFromAuthBlock = () => {
    setFlagShowVerificationBlock(false);
    setFlagShowPasswordBlock(false);
    setOtp("");
  };

  const handleStartTimer = () => setFlagTimerActive(true);

  const handleStopTimer = () => setFlagTimerActive(false);

  const handleResetTimer = () => {
    setFlagTimerActive(false);
    setSeconds(60);
  };
  const handleFlagOtp = () => {
    handleBackFromAuthBlock();
  };

  const contWithGoogle = async () => {
    setFlagLoading({ ...flagLoading, flagRoundLoader: true });;
    let currentUrl
    const searchParams = new URLSearchParams(window.location.search); let redirect = searchParams.get("redirect")
    if (redirect && redirect == "/helpdesk") {
      currentUrl = `${window.location.origin}?redirect=/helpdesk`
    }
    else if (redirect) {
      currentUrl = window.location.origin + decodeURIComponent(redirect);
    } else {
      currentUrl = window.location.href.replace("/login", "")
    }
    const response = await agent.contWithGoogle.get(currentUrl, 1);
    if (response.status) {
      if (response.data) {
        let url = response.data
        if (redirect && redirect == "/helpdesk") {
          window.location.href = url;
          // window.open(process.env.REACT_APP_HELPDESK_URL, "_self")
        }
        else {
          window.location.href = url;
        }
      }
    } else {
      recaptchaRef.current?.reset();
      showError(getErrorMessage(response?.error?.code, isDev(userDetail), response))
    }
    setTimeout(() => {
      setFlagLoading({ ...flagLoading, flagRoundLoader: false });
    }, 500)
  };


  const handleCredentialResponse = async (response: any) => {
    response.chatbot_flag = true
    let apiResponse = await OneTapLoginAuthCallback(response)
    if (apiResponse?.status) {
      window.location.href = `${window.location.origin}`
      // navigate('/')
    }
  };

  React.useEffect(() => {
    // debugger;
    // Load the Google Sign-In client
    if (userDetail.flagUser) {
      hideGoogleOneTapLoginPopup(); // Hide Google one tap login popup If User successfully loggedIn.
    } else {
      const searchParams = new URLSearchParams(window.location.search); let redirect = searchParams.get("redirect")
      if (!vendorData || getVendorField(vendorData, "company name", "Chronox AI")?.toLowerCase() == "chronox ai") {
        if (redirect && redirect == "/helpdesk") {
          //for helpdesk domain no need for one tap login
          return
        }
        const script = document.createElement("script");
        script.src = "client.js";
        script.async = true;
        document.head.appendChild(script);

        script.onload = () => {
          window.google.accounts.id.initialize({
            client_id:
              "361992322546-k97tfp20831fuavdabsl5tei4gc2g0a9.apps.googleusercontent.com",
            callback: handleCredentialResponse
          });
          window.google.accounts.id.prompt((notification: any) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            }
          });
        };

        return () => {
          // Cleanup script element
          document.head.removeChild(script);
        };
      } 
    }

  }, [window.location.pathname, userDetail]);

  const page = [
    'Save 70% of time with AI agents transforming leads into recurring clients',
    'Combine AI with human agents for seamless live chat support',
    'Engage customers effortlessly on all messaging channels',
    'Boost engagement and enhance interactions with Voice AI.'
  ]

  const highlightText = (text: any, highlightWords: any) => {
    const regex = new RegExp(`(${highlightWords.join('|')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part: any, index: any) =>
      highlightWords.some((word: any) => word.toLowerCase() === part.toLowerCase()) ? (
        <Typography component={'span'} key={index} className={styles.highlightedText}>
          {part}
        </Typography>
      ) : (
        <Typography component={'span'} key={index} className={styles.highlightedTextPart}>
          {part}
        </Typography>
      )
    );
  };

  const innerHeight = window.innerHeight;

  const onKeyDownOfEmail = (e:  React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key?.toLowerCase() == "enter") {
      e.preventDefault();
      
      const isValidEmail = validator.isEmail((user?.email ?? '').trim());
      if(isValidEmail){
        if(process.env.REACT_APP_ENV === "staging" || user?.google_recaptcha_token !== ""){
          handleAuth();
        }
      } else {
        setFlagEmailValid(false);
      }
    }
  }


  return (

    <Grid container>
      <Grid item className={styles.rightBlockWrapper}>
        <Grid container className={styles.rightBlock} >
          <Grid item xs={11} className={styles.whiteLogoStyleItem} >
            <img 
              src={getVendorField(vendorData, "company white logo", chronoxWhiteLogo)} 
              className={styles.whiteLogoStyle} 
              alt={getVendorField(vendorData, "company name", "Chronox AI")} 
            />
          </Grid>
          <Grid item xl={9.3} lg={10} md={10.5} sm={12} xs={12} className={styles.bannerContentItem} >
            <Grid container className={styles.bannerContentContainer} >
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12} className={styles.bannerTextContainer}>
                    <Typography component="span" className={styles.bannerText}>
                      Grow with exceptional customer service
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className={styles.subBannerTextContainer}>
                    <Typography component="span" className={styles.subBannerText}>
                      Maximize smart, personalized interactions with AI automations
                    </Typography>
                  </Grid>
                  {page.map((ele, index) => (
                    <Grid item xs={12} key={index} className={styles.listItemContainer}>
                      <Grid container className={styles.listItemWrapper}>
                        <Grid item className={styles.iconContainer}>
                          <img src={points} alt="points" className={styles.listIcon} />
                        </Grid>
                        <Grid item className={styles.textContainer}>
                          <Typography component={'span'} className={styles.listText}>
                            {highlightText(ele, ['Save 70%', 'live chat support', 'Engage customers', 'Voice AI.'])}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}

                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={11} className={styles.metaVerifiedContainer}>
            <img src={meta} className={styles.metaVerifiedImage} alt="Meta Verified" />
          </Grid>

          <Grid item className={styles.oppositeToMetaLogo}>
            <img src={rightImage} alt="rightCurveArc" />
          </Grid>
        </Grid>
      </Grid>

      <Grid item className={styles.leftBlockWrapper} >
        
        {isStagingEnv() && (
          <Chip
            label="Dev Environment"
            size="small"
            className={styles.devEnvironmentBadge}
          />
        )}

        <Grid container direction="column" className={styles.leftBlock} flexWrap={"nowrap"} sx={{ maxHeight: `${innerHeight}px !important` }}>
          <Grid item className={styles.chronoxLogoWrapperSecond}>
            <img src={getVendorField(vendorData, "company primary logo", chronoxLogo)} alt="logo" className={styles.logoStyle} />
          </Grid>
          <Grid item className={globalStyles.authLabel}>{`Get Started with ${getVendorField(vendorData, "company name", "Chronox AI")}`}</Grid>
          {!flagShowVerificationBlock && !flagShowPasswordBlock && (!vendorData || getVendorField(vendorData, "company name", "Chronox AI")?.toLowerCase() == "chronox ai") &&
            <>
              <Button onClick={contWithGoogle} className={globalStyles.continueWithGoogleButton}>
                <img src={googleIcon} alt="google" />
                <div className={globalStyles.googleButtonText}>
                  Continue with Google
                </div>
              </Button>
              <Divider className={`${globalStyles.fullwidth} ${globalStyles.dividerStyle} ${globalStyles.emailDividerStyle}`}>OR</Divider>
            </>
          }
          <Grid item>
          </Grid>

          <Box className={globalStyles.fullwidthWithMargin} >
            {(!flagShowVerificationBlock && !flagShowPasswordBlock) ?
              (<>
                <Grid container direction={"column"}>
                  {/* user email textfield */}
                  <InputLabel className={globalStyles.emailLabel}>Email</InputLabel>
                  <GlobalTextField
                    className={globalStyles.globalTextfield}
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    name="email"
                    placeholder="Enter Email"
                    autoComplete="email"
                    autoFocus
                    value={user.email}
                    onChange={(e) => {
                      setUser({ ...user, email: e.target.value?.trim() });
                      removeErrorSnackbar();
                      setFlagEmailValid(true)
                    }}
                    onBlur={() => {
                      user.email.length > 0 &&
                        // setFlagEmailValid(emailRegex.test(user.email));                      
                        setFlagEmailValid(validator.isEmail(user.email.trim()));
                    }}
                    onKeyDown={(e) => onKeyDownOfEmail(e)}
                    error={!flagEmailValid} // Apply error styling if user_email is invalid
                    helperText={
                      !flagEmailValid && "Please enter a valid Email Address"
                    }
                  />
                  <Grid item>
                  </Grid>
                  {/* recaptcha block */}
                  {process.env.REACT_APP_ENV !== "staging" &&
                    <Grid item className={styles.recaptchaBlock}>
                      <ReCAPTCHA
                        className={styles.mainRecaptchaBlock}
                        ref={recaptchaRef}
                        sitekey={SiteKey}
                        onChange={(value: any) => {
                          setUser({ ...user, google_recaptcha_token: value });
                        }}
                      />
                    </Grid>}
                  {/* cont button */}
                  <Grid item className={`${globalStyles.fullwidth} ${process.env.REACT_APP_ENV === "staging" ? globalStyles.stagingMargin : ""}`}>
                    <GlobalButton
                      id="authButtonStyle"
                      flagFullWidth
                      buttonText="Continue"
                      disabled={
                        process.env.REACT_APP_ENV !== "staging" ?
                          (user.email !== "" && user.google_recaptcha_token !== "" && flagEmailValid
                            ? false
                            : true)
                          :
                          (user.email !== "" && flagEmailValid
                            ? false
                            : true)
                      }
                      className="authButtonStyle"
                      onClick={() => {
                        handleAuth();
                      }}
                    />
                  </Grid>
                </Grid>
              </>)
              :
              (
                <>
                  <Grid container>
                    <Grid item className={styles.verificationBlockGrid}>
                      <Grid container className={`${globalStyles.clickable} ${globalStyles.backIconContainer}`} onClick={handleBackFromAuthBlock} >
                        <Grid item className={globalStyles.backIconItem} >
                          <img src={back} alt="backIcon" />
                        </Grid>
                        <Grid item>
                          <Typography className={globalStyles.backText} >Back</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item className={`${styles.fadeInRightAnimation} ${globalStyles.fullwidth}`}>
                      <VerificationComponent
                        value={user.email}
                        flagInvalidOtp={flagInvalidOtp}
                        flagShowPasswordBlock={flagShowPasswordBlock}
                        onChange={handleBackFromAuthBlock}
                        disabled={flagShowVerificationBlock || flagShowPasswordBlock}
                        onClick={handleLogin}
                        handleResendOTP={handleReSendOtp}
                        handleStartTimer={handleStartTimer}
                        handleStopTimer={handleStopTimer}
                        handleLogin={handleLogin}
                        handleResetTimer={handleResetTimer}
                        seconds={seconds}
                        minutes={minutes}
                        remainingSeconds={remainingSeconds}
                        flagTimerActive={flagTimerActive}
                        InputProps={{
                          endAdornment: (flagShowVerificationBlock || flagShowPasswordBlock) && (
                            <IconButton className={styles.editIconStyle} onClick={handleFlagOtp}>
                              <LogoComponent />
                            </IconButton>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                </>
              )
            }
          </Box>
        </Grid>
      </Grid>
    </Grid >
  );
}
